import { abilities, buildings, getUnit, sectors, units } from '../data/content';
import type { Faction } from '../data/types';
import type { Entity, GameSnapshot } from '../simulation/game';
import type { Point } from '../core/math';
import type { CameraBounds } from '../rendering/battlefield';

export interface PanelActions {
  request(building: number, unit: string): void;
  cancelRequest(building: number, request: number): void;
  rally(building: number): void;
  build(id: string): void;
  retreat(): void;
  pause(): void;
  menu(): void;
  ability(id: string): void;
  support(id: string): void;
  exitGarrison(building: number): void;
  selectPassenger(id: number): void;
  minimap(point: Point): void;
  camera(): CameraBounds | undefined;
  colorblindPalette(): boolean;
}
export interface PanelMetrics {
  fps: number;
  tickMs: number;
  frameMs: number;
  pathJobs: number;
  debug: boolean;
}
export class Panels {
  private initialized = false;
  private lastDomTime = -Infinity;
  private lastEventHtml = '';
  private lastSelectionHtml = '';
  private lastCommandHtml = '';
  private selectedBuildingId: number | undefined;
  private transientNotice: { text: string; expiresAt: number } | undefined;
  public constructor(
    private readonly root: HTMLElement,
    private readonly actions: PanelActions,
  ) {}
  public initialize(): HTMLElement {
    if (!this.initialized) {
      this.root.innerHTML =
        '<header id="top-bar"><span class="logo">HYPERWAR <i>RTS</i></span><span id="hud-supply"></span><span id="hud-command"></span><span id="hud-units"></span><span id="hud-depots"></span><span id="hud-victory"></span><span id="hud-time"></span><small id="hud-dev"></small><button id="pause-button" data-action="pause">Pause</button></header><main id="game-area"><section id="battlefield-host" aria-label="Battlefield"></section><aside id="event-feed"></aside></main><footer id="command-interface"><section id="minimap-panel"><h2>Kharkiv Approaches</h2><canvas id="minimap" width="210" height="118" aria-label="Tactical minimap"></canvas><p>WASD / middle drag: pan<br>Wheel: zoom · T: retreat<br>Ctrl+1–9: groups · double-tap: center</p></section><section id="selection-panel"></section><section id="command-panel"></section></footer><div id="modal-layer"></div>';
      this.root.addEventListener('click', (event) => this.handleClick(event));
      const minimap = this.root.querySelector<HTMLCanvasElement>('#minimap');
      minimap?.addEventListener('pointerdown', (event) => {
        minimap.setPointerCapture(event.pointerId);
        this.handleMinimap(event, minimap);
      });
      minimap?.addEventListener('pointermove', (event) => {
        if (event.buttons === 1) this.handleMinimap(event, minimap);
      });
      this.initialized = true;
    }
    const host = this.root.querySelector<HTMLElement>('#battlefield-host');
    if (!host) throw new Error('Battlefield host did not initialize.');
    return host;
  }
  public invalidate(): void {
    this.lastDomTime = -Infinity;
  }
  public draw(snapshot: GameSnapshot, faction: Faction, metrics: PanelMetrics): void {
    this.initialize();
    // DOM panels are intentionally throttled; Pixi renders every frame while command controls stay stable.
    if (snapshot.time - this.lastDomTime < 0.25) return;
    this.lastDomTime = snapshot.time;
    const player = snapshot.players[faction];
    const selected = snapshot.entities.filter((entity) => entity.selected);
    const selectedBuilding = selected.find((entity) => entity.kind === 'building');
    this.selectedBuildingId = selectedBuilding?.id;
    const top = this.root.querySelector<HTMLElement>('#top-bar');
    const events = this.root.querySelector<HTMLElement>('#event-feed');
    const selection = this.root.querySelector<HTMLElement>('#selection-panel');
    const commands = this.root.querySelector<HTMLElement>('#command-panel');
    if (!top || !events || !selection || !commands) return;
    top.querySelector('#hud-supply')!.innerHTML =
      `Supply <b>${Math.floor(player.supply)}</b> <small>(+${Math.round((1.25 + player.depots * 0.75) * 60)}/m)</small>`;
    top.querySelector('#hud-command')!.innerHTML =
      `Command <b>${player.capacity + player.reservedCapacity}/120</b>`;
    top.querySelector('#hud-units')!.innerHTML =
      `Units <b>${player.entities + player.reservedEntities}/40</b>`;
    top.querySelector('#hud-depots')!.innerHTML =
      `Depots <b>${player.depots}</b> <small>${player.cutoffs ? `(${player.cutoffs} cut off)` : ''}</small>`;
    top.querySelector('#hud-victory')!.textContent =
      snapshot.mode === 'sector_control' ? `Tickets ${player.tickets}` : 'Annihilation';
    top.querySelector('#hud-time')!.textContent =
      `⏱ ${Math.floor(snapshot.time / 60)}:${String(Math.floor(snapshot.time % 60)).padStart(2, '0')}`;
    top.querySelector('#hud-dev')!.textContent = metrics.debug
      ? `DEV ${metrics.fps} FPS · frame ${metrics.frameMs.toFixed(1)} ms · tick ${metrics.tickMs.toFixed(1)} ms · entities ${snapshot.entities.length} · effects ${snapshot.effects.length} · paths ${metrics.pathJobs} · visibility ${snapshot.performance.visibilityMs.toFixed(1)} ms · AI ${snapshot.performance.aiMs.toFixed(1)} ms`
      : 'F3: performance overlay';
    const notice =
      this.transientNotice && performance.now() < this.transientNotice.expiresAt
        ? `<p class="notice">${this.transientNotice.text}</p>`
        : '';
    if (!notice) this.transientNotice = undefined;
    const eventHtml = `<h2>Field reports</h2>${notice}${snapshot.messages.map((message) => `<p>${message}</p>`).join('') || '<p>Capture territory and build command infrastructure.</p>'}`;
    const selectionHtml = this.selection(selected, snapshot.entities);
    const commandHtml = this.commands(snapshot, faction, selectedBuilding);
    if (this.lastEventHtml !== eventHtml) {
      events.innerHTML = eventHtml;
      this.lastEventHtml = eventHtml;
    }
    if (this.lastSelectionHtml !== selectionHtml) {
      selection.innerHTML = selectionHtml;
      this.lastSelectionHtml = selectionHtml;
    }
    if (this.lastCommandHtml !== commandHtml) {
      commands.innerHTML = commandHtml;
      this.lastCommandHtml = commandHtml;
    }
    this.drawMinimap(snapshot, faction, this.actions.camera(), this.actions.colorblindPalette());
  }
  public notify(text: string): void {
    this.transientNotice = { text, expiresAt: performance.now() + 5000 };
    const events = this.root.querySelector<HTMLElement>('#event-feed');
    if (events)
      events.innerHTML = `<h2>Field reports</h2><p class="notice">${text}</p>${events.innerHTML.replace(/^<h2>Field reports<\/h2>/, '')}`;
  }
  public setPaused(paused: boolean): void {
    const button = this.root.querySelector<HTMLButtonElement>('#pause-button');
    if (!button) return;
    button.textContent = paused ? 'Resume' : 'Pause';
    button.setAttribute('aria-pressed', String(paused));
  }
  private handleClick(event: MouseEvent): void {
    const button = (event.target as Element | null)?.closest<HTMLButtonElement>(
      'button[data-action]',
    );
    const action = button?.dataset.action;
    if (!action) return;
    const building = this.selectedBuildingId;
    if (action === 'pause') this.actions.pause();
    if (action === 'retreat') this.actions.retreat();
    if (action === 'menu') this.actions.menu();
    if (action.startsWith('build:')) this.actions.build(action.slice(6));
    if (action.startsWith('request:') && building !== undefined)
      this.actions.request(building, action.slice(8));
    if (action.startsWith('cancel:') && building !== undefined)
      this.actions.cancelRequest(building, Number(action.slice(7)));
    if (action === 'rally' && building !== undefined) this.actions.rally(building);
    if (action.startsWith('ability:')) this.actions.ability(action.slice(8));
    if (action.startsWith('support:')) this.actions.support(action.slice(8));
    if (action === 'exit-garrison' && building !== undefined) this.actions.exitGarrison(building);
    if (action.startsWith('passenger:')) this.actions.selectPassenger(Number(action.slice(10)));
  }
  private selection(selected: Entity[], allEntities: Entity[]): string {
    if (!selected.length)
      return '<h2>Selection</h2><p>Select troops, vehicles, or a command building. Double-click a unit type to select visible matching units.</p>';
    if (selected.length > 1)
      return `<h2>Selection</h2><p><b>${selected.length} entities</b> selected</p><button data-action="retreat">Retreat infantry (T)</button>`;
    const entity = selected[0];
    if (!entity) return '';
    const definition = entity.kind === 'unit' ? getUnit(entity.definitionId) : undefined;
    const abilityCards =
      definition?.abilities
        .map((ability) => {
          const id = this.slug(ability);
          const cooldown = entity.cooldowns?.[id] ?? 0;
          const reason =
            cooldown > 0 ? `Recharging (${Math.ceil(cooldown)}s)` : this.abilityHint(id);
          return `<button data-action="ability:${id}" ${cooldown > 0 ? 'disabled' : ''} title="${reason}">${ability}<small>${reason}</small></button>`;
        })
        .join('') ?? '';
    const passengerCards = entity.passengers?.length
      ? `<section class="passengers"><h3>Passengers ${entity.passengers.length}/${entity.transportSlots ?? 0}</h3>${entity.passengers
          .map((id) => allEntities.find((candidate) => candidate.id === id))
          .filter((passenger): passenger is Entity => Boolean(passenger))
          .map(
            (passenger) =>
              `<button data-action="passenger:${passenger.id}" title="Inspect ${passenger.name}">${passenger.name}<small>${passenger.members ?? 1} members · ${Math.ceil(passenger.health)}/${passenger.maxHealth} HP</small></button>`,
          )
          .join('')}</section>`
      : '';
    const canRetreat =
      entity.kind === 'unit' &&
      (entity.category === 'infantry' ||
        (entity.definitionId === 'eq_pegasus_assault' && Boolean(entity.landed)));
    return `<h2>${entity.name}</h2><p>${definition?.role ?? entity.kind} · ${entity.order}${entity.shield ? ` · Shield ${Math.ceil(entity.shield)}` : ''}</p><div class="health"><span style="width:${Math.max(0, (entity.health / entity.maxHealth) * 100)}%"></span></div><p>${Math.ceil(entity.health)} / ${entity.maxHealth} HP${entity.members ? ` · ${entity.members}/${entity.maxMembers} members · Suppression ${Math.floor(entity.suppression ?? 0)}` : ''} · Armor ${entity.armor} · Cover ${entity.cover ?? 'none'} · Vet ${entity.veterancy} <small class="vet-tip" title="Accuracy +${Math.round(entity.veterancy * 4)}%, Capture +${Math.round(entity.veterancy * 8)}%, Suppression recovery +${(entity.veterancy * 1.25).toFixed(1)}x${entity.category === 'infantry' ? `, Dmg taken -${Math.round(entity.veterancy * 5)}%` : ''}">(?)</small>${entity.critical ? ` · ${entity.critical} critical` : ''}${entity.passengers?.length ? ` · Passengers ${entity.passengers.length}/${entity.transportSlots ?? 0}` : ''}</p>${canRetreat ? '<button data-action="retreat">Retreat (T)</button>' : ''}<div class="abilities">${abilityCards}</div>${passengerCards}`;
  }
  private commands(snapshot: GameSnapshot, faction: Faction, building: Entity | undefined): string {
    if (building) {
      if (building.owner !== faction) {
        return building.garrisonSlots
          ? `<h2>${building.name}</h2><p>Garrison: ${building.occupants?.length ?? 0}/${building.garrisonSlots}</p>${building.occupants?.length ? '<button data-action="exit-garrison">Exit garrison</button>' : '<p>Right-click this building with nearby infantry to garrison it.</p>'}`
          : `<h2>${building.name}</h2><p>Enemy or neutral structure.</p>`;
      }
      const available = units.filter(
        (unit) =>
          unit.faction === faction &&
          (unit.source === building.definitionId ||
            (building.definitionId === (faction === 'equestria' ? 'eq_hq' : 'id_hq') &&
              (unit.id.includes('rifle') || unit.id.includes('engineer')))),
      );
      const supportCards = abilities
        .filter((ability) => ability.source === building.definitionId)
        .map((ability) => {
          const headquarters = snapshot.entities.find(
            (entity) =>
              entity.owner === faction &&
              entity.definitionId === (faction === 'equestria' ? 'eq_hq' : 'id_hq'),
          );
          const cooldown = headquarters?.cooldowns?.[ability.id] ?? 0;
          const reason =
            snapshot.players[faction].supply < ability.supplyCost
              ? 'Insufficient Supply'
              : cooldown > 0
                ? `Recharging (${Math.ceil(cooldown)}s)`
                : 'Choose a target point';
          return `<button class="card" data-action="support:${ability.id}" ${reason === 'Choose a target point' ? '' : 'disabled'} title="${reason}"><b>${ability.name}</b><span>${ability.supplyCost} Supply · ${ability.cooldown}s CD</span><small>${reason}</small></button>`;
        })
        .join('');
      const cards =
        available
          .map((unit) => {
            const player = snapshot.players[faction];
            const disabled = building.construction
              ? 'Building disabled'
              : (building.queue?.length ?? 0) >= 5
                ? 'Deployment queue full'
                : player.supply < unit.cost
                  ? 'Insufficient Supply'
                  : player.capacity + player.reservedCapacity + unit.capacity > 120
                    ? 'Capacity exceeded'
                    : player.entities + player.reservedEntities >= 40
                      ? 'Entity cap reached'
                      : '';
            return `<button class="card" data-action="request:${unit.id}" ${disabled ? 'disabled' : ''} title="${disabled || unit.role}"><b>${unit.name}</b><span>${unit.cost} Supply · ${unit.requestTime}s</span><small>${disabled || unit.role}</small></button>`;
          })
          .join('') ||
        supportCards ||
        '<p>This command building unlocks support abilities.</p>';
      const queue =
        building.queue
          ?.map((request) => {
            const unit = getUnit(request.unitId);
            return `<li>${unit?.name ?? request.unitId} — ${Math.floor(request.elapsed)}s <button data-action="cancel:${request.id}">Cancel</button></li>`;
          })
          .join('') || '<li>Deployment queue empty</li>';
      const garrison = building.garrisonSlots
        ? `<p>Garrison: ${building.occupants?.length ?? 0}/${building.garrisonSlots}</p>${building.occupants?.length ? '<button data-action="exit-garrison">Exit garrison</button>' : ''}`
        : '';
      return `<h2>Request Reinforcement</h2>${garrison}<button data-action="rally">Set Rally Point</button><div class="cards">${cards}</div><h3>Deployment Queue</h3><ol>${queue}</ol>`;
    }
    const options = buildings.filter(
      (entry) =>
        entry.faction === faction &&
        !snapshot.entities.some(
          (entity) => entity.owner === faction && entity.definitionId === entry.id,
        ),
    );
    const engineerSelected = snapshot.entities.some(
      (entity) =>
        entity.owner === faction && entity.selected && entity.definitionId.includes('engineer'),
    );
    const cards = options
      .map((buildingDef) => {
        const missing = buildingDef.prerequisites.find(
          (prerequisite) =>
            !snapshot.entities.some(
              (entity) =>
                entity.owner === faction &&
                entity.definitionId === prerequisite &&
                !entity.construction,
            ),
        );
        const reason = !engineerSelected
          ? 'Select a combat engineer'
          : missing
            ? 'Prerequisite missing'
            : snapshot.players[faction].supply < buildingDef.cost
              ? 'Insufficient Supply'
              : '';
        return `<button class="card" data-action="build:${buildingDef.id}" ${reason ? 'disabled' : ''} title="${reason || 'Construct command building'}"><b>${buildingDef.name}</b><span>${buildingDef.cost} Supply · ${buildingDef.buildTime}s</span><small>${reason || 'Available for construction'}</small></button>`;
      })
      .join('');
    return `<h2>Command Infrastructure</h2><p>Select an engineer, then choose a command building. It is placed in your base sector.</p><div class="cards">${cards}</div>`;
  }
  private slug(value: string): string {
    return value
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '_')
      .replaceAll(/^_|_$/g, '');
  }
  private abilityHint(id: string): string {
    if (/hold_fire|deploy_|observation_|sensor_mast|mast_sensor|prepared_ambush/.test(id))
      return 'Toggle tactical stance';
    if (/evasive|rapid_relocation|countermeasure|command_aura/.test(id))
      return 'Activate immediately';
    if (/land|take_off|rapid_landing/.test(id)) return 'Choose a landing zone';
    if (/heal|repair|casualty_evacuation|aegis|recover_wreck|salvage/.test(id))
      return 'Choose a nearby friendly target';
    return 'Choose a target point';
  }
  private drawMinimap(
    snapshot: GameSnapshot,
    faction: Faction,
    camera: CameraBounds | undefined,
    colorblindPalette: boolean,
  ): void {
    const canvas = this.root.querySelector<HTMLCanvasElement>('#minimap');
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.fillStyle = '#26352b';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#504a40';
    context.fillRect(0, (680 / 1600) * canvas.height, canvas.width, (170 / 1600) * canvas.height);
    context.fillStyle = '#635d50';
    context.fillRect(0, (730 / 1600) * canvas.height, canvas.width, (55 / 1600) * canvas.height);
    context.fillStyle = '#4a463e';
    context.fillRect((1300 / 4096) * canvas.width, 0, (115 / 4096) * canvas.width, canvas.height);
    for (const sector of snapshot.sectors) {
      const definition = sectors.find((entry) => entry.id === sector.id);
      if (!definition) continue;
      const x = (definition.point.x / 4096) * canvas.width;
      const y = (definition.point.y / 1600) * canvas.height;
      context.fillStyle =
        sector.owner === 'equestria'
          ? colorblindPalette
            ? '#4f8edb'
            : '#a96bd4'
          : sector.owner === 'interdef'
            ? colorblindPalette
              ? '#e39a3b'
              : '#8aa377'
            : '#8a8a78';
      context.beginPath();
      context.arc(x, y, definition.type === 'victory' ? 4 : 3, 0, Math.PI * 2);
      context.fill();
    }
    for (const entity of snapshot.entities) {
      if (
        entity.embarkedIn ||
        (entity.owner !== faction && entity.owner !== 'neutral' && !entity.visible)
      )
        continue;
      context.fillStyle =
        entity.owner === faction ? '#f3d06b' : colorblindPalette ? '#e39a3b' : '#c76b5d';
      context.fillRect(
        (entity.position.x / 4096) * canvas.width - 1,
        (entity.position.y / 1600) * canvas.height - 1,
        3,
        3,
      );
    }
    for (const effect of snapshot.effects.filter((effect) => effect.kind === 'damage')) {
      context.strokeStyle = '#f08a48';
      context.lineWidth = 1.5;
      context.beginPath();
      context.arc(
        (effect.position.x / 4096) * canvas.width,
        (effect.position.y / 1600) * canvas.height,
        Math.max(3, (effect.radius / 4096) * canvas.width),
        0,
        Math.PI * 2,
      );
      context.stroke();
    }
    if (camera) {
      context.strokeStyle = '#f3d06b';
      context.lineWidth = 1.5;
      context.strokeRect(
        (camera.x / 4096) * canvas.width,
        (camera.y / 1600) * canvas.height,
        (camera.width / 4096) * canvas.width,
        (camera.height / 1600) * canvas.height,
      );
    }
  }
  private handleMinimap(event: PointerEvent, canvas: HTMLCanvasElement): void {
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    this.actions.minimap({ x: x * 4096, y: y * 1600 });
  }
}
